import { NoxnLogo } from "@/components/noxn/NoxnLogo";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Loader2, Check, Eye, EyeOff, User, Building2, Mail, Lock } from "lucide-react";

import { useAuth, storeDeviceToken } from "@/lib/auth";
import { usePageMeta } from "@/hooks/usePageMeta";

const CODE_LEN = 6;
const INK = "#1E3A6F";
const ACCENT = "#FF5A00";
const INK_TINT = (a) => `rgba(30,58,111,${a})`;

/**
 * NOXN account creation — light theme matching /v2 landing.
 * Only the essentials: name → business (optional) → email → password →
 * one-time code. No aside hero, no scanlines, no dark backdrop.
 */
export default function Signup() {
    usePageMeta({ title: "Create your free account · NOXN", description: "Create a free NOXN account to see live screen rates, book ad space and manage your campaigns." });
    const { user, registerAccount, verifyCode, trustDevice } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const next = params.get("next") || "/account";

    const [step, setStep] = useState(0);
    const [form, setForm] = useState({
        name: "", business_name: "", email: params.get("email") || "", password: "", confirm: "",
    });
    const [showPw, setShowPw] = useState(false);
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);
    const [cooldown, setCooldown] = useState(0);
    const codeRef = useRef(null);

    useEffect(() => { if (user) navigate(next, { replace: true }); }, [user, next, navigate]);
    useEffect(() => {
        if (!cooldown) return;
        const t = setTimeout(() => setCooldown((v) => Math.max(0, v - 1)), 1000);
        return () => clearTimeout(t);
    }, [cooldown]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submitDetails = async (e) => {
        e?.preventDefault?.();
        if (!form.name.trim()) { toast.error("Enter your name."); return; }
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) { toast.error("Enter a valid email."); return; }
        if (form.password.length < 8) { toast.error("Password must be at least 8 characters."); return; }
        if (form.password !== form.confirm) { toast.error("Passwords don’t match."); return; }
        setBusy(true);
        try {
            await registerAccount({
                name: form.name.trim(),
                business_name: form.business_name.trim() || null,
                email: form.email.trim(),
                password: form.password,
            });
            setStep(1);
            setCooldown(30);
            setTimeout(() => codeRef.current?.focus(), 60);
            toast.success("Verification code sent to your email.");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = typeof detail === "string" ? detail : "Could not create your account.";
            toast.error(msg);
        } finally { setBusy(false); }
    };

    const resend = async () => {
        setBusy(true);
        try {
            await registerAccount({
                name: form.name.trim(), business_name: form.business_name.trim() || null,
                email: form.email.trim(), password: form.password,
            });
            setCooldown(30);
            toast.success("New code sent.");
        } catch (err) { toast.error("Could not resend the code."); }
        finally { setBusy(false); }
    };

    const submitCode = async (e) => {
        e?.preventDefault?.();
        if (code.length !== CODE_LEN) return;
        setBusy(true);
        try {
            await verifyCode(form.email.trim(), code, true);
            try {
                const token = await trustDevice();
                storeDeviceToken(form.email.trim(), token);
            } catch (err) { /* non-fatal */ }
            toast.success("Account created. Welcome to NOXN.");
            navigate(next, { replace: true });
        } catch (err) {
            const msg = err?.response?.data?.detail || "Could not verify the code.";
            toast.error(typeof msg === "string" ? msg : "Could not verify the code.");
            setCode("");
        } finally { setBusy(false); }
    };

    const fieldStyle = { borderColor: INK_TINT(0.18), boxShadow: "0 1px 2px rgba(30,58,111,0.04)" };

    return (
        <div className="min-h-screen bg-white font-body" style={{ color: INK }} data-testid="signup-page">
            <header className="fixed inset-x-0 top-0 z-40 bg-white/85 backdrop-blur border-b" style={{ borderColor: INK_TINT(0.08) }}>
                <div className="mx-auto max-w-5xl px-6 lg:px-10 h-16 flex items-center justify-between">
                    <Link to="/" data-testid="signup-back" className="flex items-center gap-3 group">
                        <NoxnLogo variant="navy" className="h-5 w-auto transition-transform duration-300 group-hover:-rotate-1" />
                    </Link>
                    <Link to="/login" data-testid="signup-login-link"
                        className="text-[13px] transition-colors" style={{ color: INK_TINT(0.7) }}>
                        <span className="hover:text-[color:var(--ink)]" style={{ ["--ink"]: INK }}>Already have an account? Sign in →</span>
                    </Link>
                </div>
            </header>

            <main className="max-w-md mx-auto px-6 pt-40 pb-24 relative">
                <div className="pointer-events-none absolute -top-24 -right-16 w-[380px] h-[380px] rounded-full opacity-25 blur-[100px]"
                    style={{ background: `radial-gradient(circle, ${ACCENT}66, transparent 60%)` }} />

                <motion.div
                    initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                    className="text-[11px] uppercase tracking-[0.28em] mb-6 inline-flex items-center gap-2"
                    style={{ color: INK_TINT(0.55) }}
                >
                    <motion.span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: ACCENT }}
                        animate={{ opacity: [1, 0.35, 1], scale: [1, 1.2, 1] }} transition={{ duration: 2.4, repeat: Infinity }} />
                    {step === 0 ? "Create your free account" : "Verify your email"}
                </motion.div>

                <AnimatePresence mode="wait">
                    {step === 0 && (
                        <motion.form key="details" onSubmit={submitDetails}
                            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                            data-testid="signup-step-details"
                        >
                            <h1 className="font-display tracking-[-0.03em] text-5xl leading-[0.98] font-medium" style={{ color: INK }}>
                                Start<br />
                                <span style={{ color: ACCENT }}>in seconds.</span>
                            </h1>
                            <p className="mt-6 text-[15px] leading-relaxed" style={{ color: INK_TINT(0.65) }}>
                                Free forever. You&apos;ll only pay for the plays you book.
                            </p>

                            <div className="mt-10 space-y-4">
                                <Field label="Your name" testid="signup-name" icon={User}
                                    value={form.name} onChange={set("name")} placeholder="Jane Smith" autoFocus />
                                <Field label="Business (optional)" testid="signup-business" icon={Building2}
                                    value={form.business_name} onChange={set("business_name")} placeholder="Acme Coffee Co." />
                                <Field label="Email" type="email" testid="signup-email" icon={Mail}
                                    value={form.email} onChange={set("email")} placeholder="you@company.com" />

                                <div>
                                    <label className="text-[11px] uppercase tracking-[0.28em]" style={{ color: INK_TINT(0.55) }}>
                                        Password
                                    </label>
                                    <div className="mt-2 flex items-center gap-2 rounded-2xl border px-4 py-3.5 transition-all focus-within:border-[color:var(--acc)]"
                                        style={{ ...fieldStyle, ["--acc"]: ACCENT }}>
                                        <Lock size={15} style={{ color: INK_TINT(0.55) }} />
                                        <input
                                            type={showPw ? "text" : "password"}
                                            value={form.password}
                                            onChange={set("password")}
                                            placeholder="At least 8 characters"
                                            data-testid="signup-password"
                                            className="flex-1 bg-transparent outline-none text-sm placeholder:text-[color:var(--ph)]"
                                            style={{ color: INK, ["--ph"]: INK_TINT(0.35) }}
                                        />
                                        <button type="button" onClick={() => setShowPw((s) => !s)}
                                            data-testid="signup-toggle-password"
                                            className="text-[12px] transition-colors" style={{ color: INK_TINT(0.55) }}
                                            aria-label={showPw ? "Hide password" : "Show password"}
                                        >
                                            {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                                        </button>
                                    </div>
                                </div>

                                <Field label="Confirm password" type={showPw ? "text" : "password"} testid="signup-confirm" icon={Lock}
                                    value={form.confirm} onChange={set("confirm")} placeholder="Re-enter your password" />
                            </div>

                            <button type="submit" disabled={busy}
                                data-testid="signup-submit"
                                className="group mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full py-4 text-sm font-medium text-white transition-all disabled:opacity-60"
                                style={{ backgroundColor: INK }}
                                onMouseEnter={(e) => { if (!busy) { e.currentTarget.style.backgroundColor = ACCENT; e.currentTarget.style.boxShadow = "0 18px 40px -14px rgba(255,90,0,0.55)"; } }}
                                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = INK; e.currentTarget.style.boxShadow = ""; }}
                            >
                                {busy ? <><Loader2 size={16} className="animate-spin" /> Creating</> : <>Create account <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" /></>}
                            </button>

                            <p className="mt-5 text-[12px]" style={{ color: INK_TINT(0.55) }}>
                                By continuing you agree to our{" "}
                                <a href="/terms" className="underline" style={{ color: INK, textDecorationColor: INK_TINT(0.4) }}>Terms</a> and{" "}
                                <a href="/privacy" className="underline" style={{ color: INK, textDecorationColor: INK_TINT(0.4) }}>Privacy</a>.
                            </p>
                        </motion.form>
                    )}

                    {step === 1 && (
                        <motion.form key="code" onSubmit={submitCode}
                            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                            data-testid="signup-step-code"
                        >
                            <h1 className="font-display tracking-[-0.03em] text-5xl leading-[0.98] font-medium" style={{ color: INK }}>
                                Check your<br /><span style={{ color: ACCENT }}>inbox.</span>
                            </h1>
                            <p className="mt-6 text-[15px] break-all" style={{ color: INK_TINT(0.65) }}>
                                Sent a 6-digit code to <strong style={{ color: INK }}>{form.email}</strong>.
                            </p>

                            <div className="mt-10">
                                <label className="text-[11px] uppercase tracking-[0.28em]" style={{ color: INK_TINT(0.55) }}>One-time code</label>
                                <input
                                    ref={codeRef} type="text" inputMode="numeric" pattern="[0-9]*" maxLength={CODE_LEN}
                                    value={code}
                                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, CODE_LEN))}
                                    placeholder="••••••"
                                    data-testid="signup-code-input"
                                    className="mt-2 w-full rounded-2xl border px-4 py-4 text-3xl font-mono-tech tracking-[0.6em] text-center outline-none"
                                    style={{ ...fieldStyle, color: INK, backgroundColor: "#FFFFFF" }}
                                />
                            </div>

                            <div className="mt-4 flex items-center justify-between text-[12px]">
                                <button type="button" onClick={() => { setStep(0); setCode(""); }} className="underline transition-colors"
                                    style={{ color: INK_TINT(0.7), textDecorationColor: INK_TINT(0.3) }}>
                                    ← Edit details
                                </button>
                                <button type="button" onClick={resend} disabled={cooldown > 0 || busy}
                                    data-testid="signup-resend"
                                    className="transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                    style={{ color: cooldown > 0 ? INK_TINT(0.4) : ACCENT }}
                                >
                                    {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
                                </button>
                            </div>

                            <button type="submit" disabled={busy || code.length !== CODE_LEN}
                                data-testid="signup-verify"
                                className="group mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full py-4 text-sm font-medium text-white transition-all disabled:opacity-60"
                                style={{ backgroundColor: INK }}
                                onMouseEnter={(e) => { if (!e.currentTarget.disabled) { e.currentTarget.style.backgroundColor = ACCENT; e.currentTarget.style.boxShadow = "0 18px 40px -14px rgba(255,90,0,0.55)"; } }}
                                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = INK; e.currentTarget.style.boxShadow = ""; }}
                            >
                                {busy ? <><Loader2 size={16} className="animate-spin" /> Verifying</> : <>Verify <Check size={16} /></>}
                            </button>
                        </motion.form>
                    )}
                </AnimatePresence>
            </main>
        </div>
    );
}

const Field = ({ label, value, onChange, icon: Icon, testid, type = "text", placeholder, autoFocus }) => (
    <div>
        <label className="text-[11px] uppercase tracking-[0.28em]" style={{ color: INK_TINT(0.55) }}>{label}</label>
        <div className="mt-2 flex items-center gap-2 rounded-2xl border px-4 py-3.5 transition-all focus-within:border-[color:var(--acc)]"
            style={{ borderColor: INK_TINT(0.18), boxShadow: "0 1px 2px rgba(30,58,111,0.04)", ["--acc"]: ACCENT }}>
            {Icon && <Icon size={15} style={{ color: INK_TINT(0.55) }} />}
            <input
                type={type} value={value} onChange={onChange}
                placeholder={placeholder} autoFocus={autoFocus} data-testid={testid}
                className="flex-1 bg-transparent outline-none text-sm placeholder:text-[color:var(--ph)]"
                style={{ color: INK, ["--ph"]: INK_TINT(0.35) }}
            />
        </div>
    </div>
);
